import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardDescription, Input, Badge, Button } from '@/components/ui'
import { useAuth } from '@/contexts/AuthContext'
import {
  FileText,
  CreditCard,
  QrCode,
  Image,
  Mail,
  Hash,
  Key,
  Link2,
  Globe,
  MessageSquare,
  Palette,
  Clock,
  Search,
  Fingerprint,
  Binary,
  Braces,
  Type,
  Sparkles,
  Lock,
  Code,
  Scale,
  PenTool,
  Timer,
  TextSelect,
  Columns,
  ALargeSmall,
  Calendar,
  Star,
  Download,
} from 'lucide-react'
import { useFavorites } from '@/hooks/useFavorites'

interface Tool {
  title: string
  description: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  category: string
  vip?: boolean
  ready?: boolean
  popular?: boolean
  authOnly?: boolean
}

export const tools: Tool[] = [
  // Special Tools - Ready
  { title: 'Temp Mail', description: 'Disposable email addresses', href: '/temp-mail', icon: Mail, category: 'Special Tools', ready: true, popular: true, authOnly: true },
  { title: 'Credit Card Generator', description: 'BIN-based test card generator', href: '/credit-card-generator', icon: CreditCard, category: 'Special Tools', ready: true },
  { title: 'IP Information', description: 'Get your public IP address', href: '/ip-info', icon: Globe, category: 'Special Tools', ready: true },
  { title: 'Card Luhn Validator', description: 'Validate card numbers', href: '/luhn-validator', icon: CreditCard, category: 'Special Tools', ready: true },
  { title: 'Identity Generator', description: 'Generate fake identities for testing', href: '/identity', icon: Fingerprint, category: 'Special Tools', ready: true },
  { title: 'cURL Converter', description: 'Convert cURL to any language', href: '/curl-converter', icon: Code, category: 'Special Tools', ready: true },

  // Developer Tools
  { title: 'JSON Formatter', description: 'Format and validate JSON', href: '/json-formatter', icon: Braces, category: 'Developer Tools', ready: true },
  { title: 'Timestamp Converter', description: 'Convert Unix timestamps', href: '/timestamp', icon: Clock, category: 'Developer Tools', ready: true },
  { title: 'Hash Generator', description: 'MD5, SHA-256, etc.', href: '/hash-generator', icon: Hash, category: 'Developer Tools', ready: true },
  { title: 'UUID Generator', description: 'Generate and validate UUIDs', href: '/uuid-generator', icon: Fingerprint, category: 'Developer Tools', ready: true },
  { title: 'URL Encoder/Decoder', description: 'Encode or decode URLs', href: '/url-encoder-decoder', icon: Link2, category: 'Developer Tools' },
  { title: 'Unit Converter', description: 'Convert various units', href: '/unit-converter', icon: Scale, category: 'Developer Tools', ready: true },
  { title: 'Pomodoro Timer', description: 'Stay focused and productive', href: '/pomodoro-timer', icon: Timer, category: 'Developer Tools' },
  { title: 'ASCII Art Generator', description: 'Create text-based art', href: '/ascii-art-generator', icon: ALargeSmall, category: 'Developer Tools' },
  { title: 'Text Diff', description: 'Compare two texts', href: '/text-diff', icon: TextSelect, category: 'Developer Tools' },
  { title: 'Text Diff (Side-by-Side)', description: 'Compare texts side-by-side', href: '/text-diff-side-by-side', icon: Columns, category: 'Developer Tools' },
  { title: 'String Inspector', description: 'Get detailed information on strings', href: '/string-inspector', icon: Search, category: 'Developer Tools' },
  { title: 'CRON Job Editor', description: 'Create and validate CRON jobs', href: '/cron-editor', icon: Calendar, category: 'Developer Tools' },


  // Encryptors - Internal Routes
  { title: 'Adyen v1', description: 'With riskData', href: '/encryptor/adyen-v1', icon: Lock, category: 'Encryptors', ready: true },
  { title: 'Adyen v2', description: 'With riskData', href: '/encryptor/adyen-v2', icon: Lock, category: 'Encryptors', ready: true },
  { title: 'Cybersource v1', description: 'Encrypt', href: '/encryptor/cybersource-v1', icon: Lock, category: 'Encryptors', ready: true },
  { title: 'Cybersource v2', description: 'Encrypt', href: '/encryptor/cybersource-v2', icon: Lock, category: 'Encryptors', ready: true },
  { title: 'CARD/CVV', description: 'Encrypt', href: '/encryptor/card-cvv', icon: Lock, category: 'Encryptors', ready: true },
  { title: 'Vantiv', description: 'Encrypt', href: '/encryptor/vantiv', icon: Lock, category: 'Encryptors', ready: true },
  { title: 'Clover', description: 'Encrypt', href: '/encryptor/clover', icon: Lock, category: 'Encryptors', ready: true },
  { title: 'Eway', description: 'Encrypt', href: '/encryptor/eway', icon: Lock, category: 'Encryptors', ready: true },
  { title: 'CardConnect', description: 'Encrypt', href: '/encryptor/cardconnect', icon: Lock, category: 'Encryptors', ready: true },
  { title: 'Paytrace', description: 'Encrypt', href: '/encryptor/paytrace', icon: Lock, category: 'Encryptors', ready: true },
  { title: 'Zuora', description: 'Encrypt', href: '/encryptor/zuora', icon: Lock, category: 'Encryptors', ready: true },
  { title: 'FirstData v2', description: 'Encrypt', href: '/encryptor/firstdata-v2', icon: Lock, category: 'Encryptors', ready: true },
  { title: 'SecurePay', description: 'x Auspost', href: '/encryptor/securepay', icon: Lock, category: 'Encryptors', ready: true },
  { title: 'Auruspay', description: 'Encrypt', href: '/encryptor/auruspay', icon: Lock, category: 'Encryptors', ready: true },
  { title: 'Oceanpayment', description: 'Encrypt', href: '/encryptor/oceanpayment', icon: Lock, category: 'Encryptors', ready: true },
  { title: 'Captcha v3', description: 'Invisible Captcha', href: '/encryptor/captcha-v3', icon: Lock, category: 'Encryptors', ready: true },
  { title: 'Freedompay', description: 'Encrypt', href: '/encryptor/freedompay', icon: Lock, category: 'Encryptors', ready: true },
  { title: 'Paysimple', description: 'Encrypt', href: '/encryptor/paysimple', icon: Lock, category: 'Encryptors', ready: true },
  { title: 'BINLIST', description: 'BIN Lookup', href: '/encryptor/binlist', icon: Lock, category: 'Encryptors', ready: true },

  // Documents - Ready
  { title: 'PDF Studio', description: 'Create and edit PDF documents', href: '/pdf-studio', icon: FileText, category: 'Documents', ready: true },
  { title: 'Student Slip', description: 'Generate student billing slips', href: '/student-slip', icon: FileText, category: 'Documents', ready: true },
  { title: 'Resume Builder', description: 'Build professional resumes', href: '/resume-builder', icon: FileText, category: 'Documents', ready: true },
  { title: 'Invoice Generator', description: 'Create invoices', href: '/invoice-generator', icon: FileText, category: 'Documents', ready: true },
  { title: 'Markdown Editor', description: 'Write and preview markdown', href: '/markdown-editor', icon: FileText, category: 'Documents', ready: true },

  // Utilities - Ready
  { title: 'QR Generator', description: 'Create QR codes', href: '/qr-generator', icon: QrCode, category: 'Utilities', ready: true },
  { title: 'Password Generator', description: 'Secure passwords', href: '/password-generator', icon: Key, category: 'Utilities', ready: true },
  { title: 'Base64', description: 'Encode/decode Base64', href: '/base64', icon: Binary, category: 'Utilities', ready: true },
  { title: 'Lorem Ipsum', description: 'Placeholder text generator', href: '/lorem-ipsum', icon: FileText, category: 'Utilities', ready: true },
  { title: 'Word Counter', description: 'Count words and characters', href: '/word-counter', icon: Hash, category: 'Utilities', ready: true },
  { title: 'Text Case', description: 'Convert text case formats', href: '/text-case', icon: Type, category: 'Utilities', ready: true },
  { title: 'Image Compressor', description: 'Compress images', href: '/image-compressor', icon: Image, category: 'Utilities', ready: true },
  { title: 'Image Resizer', description: 'Resize images', href: '/image-resizer', icon: Image, category: 'Utilities', ready: true },
  { title: 'Color Picker', description: 'Pick colors from image', href: '/color-picker', icon: Palette, category: 'Utilities', ready: true },
  { title: 'URL Shortener', description: 'Shorten URLs with TinyURL', href: '/url-shortener', icon: Link2, category: 'Utilities', ready: true },
  { title: 'E-Signature', description: 'Sign PDF documents', href: '/e-signature', icon: PenTool, category: 'Utilities', ready: true },
  { title: 'Video Downloader', description: 'Download videos from any link', href: '/video-downloader', icon: Download, category: 'Utilities', ready: true },

  // SEO - Ready
  { title: 'Meta Tags', description: 'Generate SEO meta tags', href: '/meta-tags', icon: Globe, category: 'SEO', ready: true },
  { title: 'Sitemap Generator', description: 'Create sitemaps', href: '/sitemap-generator', icon: Globe, category: 'SEO', ready: true },
]

const categories = [
  { name: 'Favorites', icon: Star, description: 'Your favorite tools' },
  { name: 'Special Tools', icon: Sparkles, description: 'Premium and specialized tools' },
  { name: 'Developer Tools', icon: Code, description: 'Tools for developers' },
  { name: 'Encryptors', icon: Lock, description: 'Payment gateway encryption tools' },
  { name: 'Documents', icon: FileText, description: 'Document creation and editing' },
  { name: 'Utilities', icon: Hash, description: 'General purpose utility tools' },
  { name: 'SEO', icon: Globe, description: 'Search engine optimization tools' },
]

export function Tools() {
  const [search, setSearch] = useState('')
  const { favorites, toggleFavorite } = useFavorites()
  const { isAuthenticated } = useAuth()

  const sortedCategories = [...categories].sort((a, b) => {
    const aIsFavorite = favorites.includes(a.name);
    const bIsFavorite = favorites.includes(b.name);

    if (aIsFavorite && !bIsFavorite) {
      return -1; // a comes before b
    }
    if (!aIsFavorite && bIsFavorite) {
      return 1; // b comes before a
    }
    return 0; // maintain original order
  });

  const filteredTools = search
    ? tools.filter(
        (tool) =>
          tool.title.toLowerCase().includes(search.toLowerCase()) ||
          tool.description.toLowerCase().includes(search.toLowerCase())
      )
    : tools

  const getToolsByCategory = (category: string) => {
    if (category === 'Favorites') {
      return filteredTools.filter(tool => favorites.includes(tool.href))
    }
    return filteredTools.filter(
      (tool) => tool.category === category && !favorites.includes(tool.href)
    )
  }

  const readyCount = tools.filter((t) => t.ready).length

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">
          <span className="text-primary glow-text">{'>'}</span> All Tools
        </h1>
        <p className="text-muted-foreground">
          {readyCount} tools ready • {tools.length - readyCount} coming soon
        </p>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search tools..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Categories */}
      {sortedCategories.map((category) => {
        const categoryTools = getToolsByCategory(category.name)
        if (category.name === 'Favorites' && favorites.length === 0) return null
        if (categoryTools.length === 0) return null

        const CategoryIcon = category.icon

        return (
          <section key={category.name} className="space-y-4">
            {/* Category Header */}
            <div className="flex items-center gap-3 border-b border-border pb-3">
              <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                category.name === 'Favorites' ? 'bg-gradient-to-br from-yellow-500/20 to-amber-500/20' :
                category.name === 'Special Tools' ? 'bg-gradient-to-br from-primary/20 to-purple-500/20' :
                category.name === 'Encryptors' ? 'bg-gradient-to-br from-orange-500/20 to-red-500/20' :
                category.name === 'Developer Tools' ? 'bg-gradient-to-br from-blue-500/20 to-cyan-500/20' :
                'bg-primary/10'
              }`}>
                <CategoryIcon className={`h-5 w-5 ${
                  category.name === 'Favorites' ? 'text-yellow-500' :
                  category.name === 'Encryptors' ? 'text-orange-500' :
                  category.name === 'Developer Tools' ? 'text-blue-500' :
                  'text-primary'
                }`} />
              </div>
              <div>
                <h2 className="text-xl font-semibold">{category.name}</h2>
                <p className="text-sm text-muted-foreground">{category.description}</p>
              </div>
              <Badge variant="secondary" className="ml-auto">
                {categoryTools.length} tools
              </Badge>
            </div>

            {/* Tools Grid */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {categoryTools.map((tool) => {
                const Icon = tool.icon
                const isFavorite = favorites.includes(tool.href)

                return (
                  <div key={tool.href} className="relative">
                    <Link to={tool.href}>
                      <Card className={`glass h-full transition-all hover:border-primary/50 hover:shadow-glow cursor-pointer ${!tool.ready ? 'opacity-70' : ''}`}>
                        <CardHeader>
                          <div className="flex items-start justify-between">
                            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                              tool.category === 'Special Tools' ? 'bg-gradient-to-br from-primary/20 to-purple-500/20' :
                              tool.category === 'Encryptors' ? 'bg-gradient-to-br from-orange-500/20 to-red-500/20' :
                              tool.category === 'Developer Tools' ? 'bg-gradient-to-br from-blue-500/20 to-cyan-500/20' :
                              'bg-primary/10'
                            }`}>
                              <Icon className={`h-5 w-5 ${
                                tool.category === 'Encryptors' ? 'text-orange-500' :
                                tool.category === 'Developer Tools' ? 'text-blue-500' :
                                'text-primary'
                              }`} />
                            </div>
                            <div className="flex gap-1">
                              {tool.authOnly && !isAuthenticated && (
                                <span className="text-lg" title="Login required">🔒</span>
                              )}
                              {tool.vip && (
                                <Badge className="bg-gradient-to-r from-yellow-500 to-amber-600">
                                  VIP
                                </Badge>
                              )}
                            </div>
                          </div>
                          <CardTitle className="text-base">{tool.title}</CardTitle>
                          <CardDescription>{tool.description}</CardDescription>
                        </CardHeader>
                      </Card>
                    </Link>
                    {tool.authOnly ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-2 right-2"
                        disabled
                      >
                        <Lock className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-2 right-2"
                        onClick={(e) => {
                          e.preventDefault()
                          toggleFavorite(tool.href)
                        }}
                        disabled={!isAuthenticated}
                        title={!isAuthenticated ? 'Login to favorite tools' : ''}
                      >
                        <Star className={`h-4 w-4 ${isFavorite ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground'}`} />
                      </Button>
                    )}
                  </div>
                )
              })}
            </div>
          </section>
        )
      })}

      {filteredTools.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No tools found matching your search.</p>
        </div>
      )}
    </div>
  )
}
